import { apiClient } from '@/services'
import {
  BuildingPermit,
  BuildingPermitsData,
  PermitFilters,
  PermitSortOptions,
  BuildingPermitsStats,
} from '@popety-io/types'

export class BuildingPermitsService {
  private baseUrl = '/building-permits'

  async searchPermits(
    propertyId: string,
    filters: PermitFilters,
    sort: PermitSortOptions,
    page = 1,
    pageSize = 20,
    propertyCoordinates?: { lat: number; lon: number }
  ): Promise<BuildingPermitsData> {
    try {
      // Convert our filters to the API format
      const apiFilters: any = {}

      // Map category filter to regbl_classification_general (backend field)
      if (filters.categories && filters.categories.length > 0) {
        apiFilters.regbl_classification_general = filters.categories.map(
          (cat) => {
            // Map our categories to API regbl_classification_general values
            const categoryMap: Record<string, string> = {
              development: 'Construction nouvelle',
              renovation: 'Rénovation',
              transformation: 'Transformation',
              elevation: 'Surélévation',
              regularisation: 'Régularisation',
            }
            return categoryMap[cat] || cat
          }
        )
      }

      // Map status filter to latest_status_db (backend field)
      if (filters.statuses && filters.statuses.length > 0) {
        apiFilters.latest_status_db = filters.statuses.map((status) => {
          // Map our statuses to API latest_status_db values
          const statusMap: Record<string, string> = {
            instruction: 'En instruction',
            enregistrement: 'Enregistrement',
            approved: 'Accepté',
            pending: 'En attente',
            rejected: 'Refusé',
            completed: 'Terminé',
          }
          return statusMap[status] || status
        })
      }

      // Map date range filter - backend expects {min, max} format
      if (filters.date_range?.start || filters.date_range?.end) {
        apiFilters.inquiry_start_date = {}
        if (filters.date_range?.start) {
          apiFilters.inquiry_start_date.min = filters.date_range.start
            .toISOString()
            .split('T')[0]
        }
        if (filters.date_range?.end) {
          apiFilters.inquiry_start_date.max = filters.date_range.end
            .toISOString()
            .split('T')[0]
        }
      }

      // Map search query
      if (filters.search_query) {
        apiFilters.query = filters.search_query
      }

      // Map postal codes - use postal_codes_names (backend field)
      if (filters.postalCodes && filters.postalCodes.length > 0) {
        apiFilters.postal_codes_names = filters.postalCodes
      }

      // Map cities - use municipality_name (backend field)
      if (filters.cities && filters.cities.length > 0) {
        apiFilters.municipality_name = filters.cities
      }

      // Add geospatial filtering with property coordinates - backend expects radius format
      if (filters.radius && propertyCoordinates) {
        apiFilters.radius = {
          center: {
            lat: propertyCoordinates.lat,
            lon: propertyCoordinates.lon,
          },
          distance: `${filters.radius}m`,
        }
      }

      const requestBody = {
        filters: apiFilters,
        sort: [
          {
            field: sort.field === 'date' ? 'inquiry_start_date' : 'id',
            order: sort.direction,
          },
        ],
        limit: pageSize,
        offset: (page - 1) * pageSize,
        fields: [
          'id',
          'fao_id',
          'primary_official_id',
          'secondary_official_id',
          'description',
          'city_name',
          'district_name',
          'municipality_name',
          'postal_codes_names',
          'geo_center',
          'location',
          'lands',
          'features',
          'participants',
          'inquiry_start_date',
          'inquiry_last_date',
          'inquiry_approval_date',
          'latest_status_db',
          'official_final_decision',
          'development_classification_final',
          'regbl_classification_general',
          'land_use_principal_type',
          'land_register_number',
        ],
      }

      const response = await apiClient.post(
        `${this.baseUrl}/search`,
        requestBody
      )

      // Check for errors in the response
      if (response.errors) {
        console.error('API returned errors:', response.errors)
        throw new Error(
          response.errors[0]?.message || 'Failed to fetch building permits'
        )
      }

      // Transform API response to our format
      const apiData = response.data || {}
      const permits = (apiData.data || []).map((apiPermit: any) =>
        this.mapApiPermitToLocal(apiPermit)
      )

      return {
        permits,
        total: apiData.total || 0,
        page,
        pageSize,
        filters,
        sort,
        lastUpdated: new Date(),
      }
    } catch (error) {
      console.error('Error fetching building permits:', error)
      throw error
    }
  }

  async getPermitById(permitId: string): Promise<BuildingPermit> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${permitId}`)

      // Check for errors in the response
      if (response.errors) {
        console.error('API returned errors:', response.errors)
        throw new Error(
          response.errors[0]?.message || 'Failed to fetch permit details'
        )
      }

      if (!response.data) {
        throw new Error('No data returned from API')
      }

      return this.mapApiPermitToLocal(response.data)
    } catch (error) {
      console.error('Error fetching permit details:', error)
      throw error
    }
  }

  async getPermitStats(
    propertyId: string,
    radius = 500
  ): Promise<BuildingPermitsStats> {
    try {
      // TODO: Add geospatial filtering based on propertyId and radius
      // For now, get all permits and calculate stats
      const requestBody = {
        filters: {}, // No filters to get all permits for stats
        sort: [
          {
            field: 'inquiry_start_date',
            order: 'desc' as const,
          },
        ],
        limit: 100, // Get a reasonable number for stats calculation
        offset: 0,
        fields: ['id', 'type_name', 'result', 'inquiry_start_date', 'city'],
      }

      const response = await apiClient.post(
        `${this.baseUrl}/search`,
        requestBody
      )

      // Check for errors in the response
      if (response.errors) {
        console.error('API returned errors:', response.errors)
        throw new Error(
          response.errors[0]?.message || 'Failed to fetch permit stats'
        )
      }

      const apiData = response.data || {}
      const permits = apiData.data || []

      // Calculate stats from the results
      const stats: BuildingPermitsStats = {
        totalPermits: apiData.total || 0,
        byCategory: {
          development: 0,
          renovation: 0,
          transformation: 0,
          elevation: 0,
          regularisation: 0,
        },
        byStatus: {
          instruction: 0,
          enregistrement: 0,
          approved: 0,
          pending: 0,
          rejected: 0,
          completed: 0,
        },
        averageDistance: 325, // TODO: Calculate from actual coordinates
        recentActivity: 0,
        nearbyDevelopments: 0,
      }

      // Calculate category statistics
      permits.forEach((permit: any) => {
        const category = this.mapApiCategoryToLocal(permit.type_name)
        if (category && stats.by_category[category] !== undefined) {
          stats.by_category[category]++
        }

        const status = this.mapApiStatusToLocal(permit.result)
        if (status && stats.by_status[status] !== undefined) {
          stats.by_status[status]++
        }

        // Count recent activity (last 30 days)
        if (permit.inquiry_start_date) {
          const permitDate = new Date(permit.inquiry_start_date)
          const thirtyDaysAgo = new Date()
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

          if (permitDate > thirtyDaysAgo) {
            stats.recent_activity++
          }
        }
      })

      return stats
    } catch (error) {
      console.error('Error fetching permit stats:', error)
      throw error
    }
  }

  private mapApiCategoryToLocal(
    apiCategory: string
  ): keyof BuildingPermitsStats['byCategory'] | null {
    // Map API regbl_classification_general values to our local categories
    const categoryMap: Record<
      string,
      keyof BuildingPermitsStats['byCategory']
    > = {
      'new construction': 'development',
      'construction nouvelle': 'development',
      transformation: 'transformation',
      renovation: 'renovation',
      rénovation: 'renovation',
      demolition: 'regularisation',
      démolition: 'regularisation',
      surélévation: 'elevation',
      elevation: 'elevation',
      // Add more mappings as needed
    }
    return categoryMap[apiCategory?.toLowerCase()] || null
  }

  private mapApiStatusToLocal(
    apiStatus: string
  ): keyof BuildingPermitsStats['byStatus'] | null {
    // Map API latest_status_db and official_final_decision values to our local statuses
    const statusMap: Record<string, keyof BuildingPermitsStats['byStatus']> = {
      // latest_status_db values
      archive: 'completed',
      accepte: 'approved',
      accepté: 'approved',
      termine: 'completed',
      terminé: 'completed',
      abandonne: 'rejected',
      abandonné: 'rejected',
      caduque: 'rejected',
      refuse: 'rejected',
      refusé: 'rejected',
      // official_final_decision values
      realise: 'completed',
      réalisé: 'completed',
      chantier: 'instruction',
      recours: 'pending',
      // Legacy values
      'en instruction': 'instruction',
      enregistrement: 'enregistrement',
      'en attente': 'pending',
    }
    return statusMap[apiStatus?.toLowerCase()] || null
  }

  private mapApiPermitToLocal(apiPermit: any): BuildingPermit {
    // Extract postal code from postal_codes_names array
    const postalCode = apiPermit.postal_codes_names?.[0]?.split(' ')?.[0]

    // Build address from lands if available, otherwise use description
    const landAddress = apiPermit.lands?.[0]
    let fullAddress = apiPermit.description || ''
    if (landAddress) {
      const street = landAddress.address_street || ''
      const number = landAddress.address_number || ''
      const city = landAddress.address_city || apiPermit.city_name || ''
      const postal = landAddress.address_postal_code || postalCode || ''

      if (street || number) {
        fullAddress = `${street} ${number}`.trim()
        if (postal && city) {
          fullAddress = `${fullAddress}, ${postal} ${city}`
        } else if (city) {
          fullAddress = `${fullAddress}, ${city}`
        }
      }
    } else if (postalCode && apiPermit.city_name) {
      fullAddress = `${fullAddress}, ${postalCode} ${apiPermit.city_name}`
    } else if (apiPermit.city_name) {
      fullAddress = `${fullAddress}, ${apiPermit.city_name}`
    }

    // Extract owner and architect from participants
    const owner = apiPermit.participants?.find((p: any) =>
      p.roles?.some(
        (r: any) =>
          r.role?.toLowerCase().includes('owner') ||
          r.role?.toLowerCase().includes('propriétaire')
      )
    )?.full_name

    const architect = apiPermit.participants?.find((p: any) =>
      p.roles?.some(
        (r: any) =>
          r.role?.toLowerCase().includes('architect') ||
          r.role?.toLowerCase().includes('architecte')
      )
    )?.full_name

    return {
      id: apiPermit.id?.toString() || '',
      permitNumber:
        apiPermit.primary_official_id ||
        apiPermit.fao_id ||
        apiPermit.secondary_official_id ||
        `ID-${apiPermit.id}`,
      category:
        this.mapApiCategoryToLocal(apiPermit.regbl_classification_general) ||
        'development',
      status:
        this.mapApiStatusToLocal(
          apiPermit.latest_status_db || apiPermit.official_final_decision
        ) || 'pending',
      address: fullAddress,
      parcelNumber:
        apiPermit.lands?.[0]?.code_number ||
        apiPermit.lands?.[0]?.land_register_number ||
        apiPermit.land_register_number ||
        undefined,
      zone: undefined, // Not available in current API
      type:
        apiPermit.regbl_classification_general ||
        apiPermit.land_use_principal_type ||
        'Unknown',
      description: apiPermit.description || '',
      filingDate: apiPermit.inquiry_start_date
        ? new Date(apiPermit.inquiry_start_date)
        : new Date(),
      publicationDate: apiPermit.inquiry_start_date
        ? new Date(apiPermit.inquiry_start_date)
        : undefined,
      approvalDate:
        apiPermit.inquiry_approval_date || apiPermit.inquiry_last_date
          ? new Date(
              apiPermit.inquiry_approval_date || apiPermit.inquiry_last_date
            )
          : undefined,
      owner: owner || undefined,
      architect: architect || undefined,
      engineer: undefined, // Not available in API
      generalContractor: undefined, // Not available in API
      distance: undefined, // TODO: Calculate based on coordinates
      coordinates: apiPermit.geo_center,
      location: apiPermit.location, // Add location field for map display
      currentUse: undefined, // Not available in API
      proposedUse: undefined, // Not available in API
      authority: 'DALE - Canton de Genève',
    }
  }

  async exportPermits(
    propertyId: string,
    filters: PermitFilters,
    format: 'csv' | 'excel' | 'pdf' = 'excel'
  ): Promise<Blob> {
    try {
      // For now, we'll get the search results and create a simple export
      // TODO: Implement actual export endpoint on the backend
      const searchData = await this.searchPermits(
        propertyId,
        filters,
        { field: 'date', direction: 'desc' },
        1,
        1000
      )

      // Create a simple CSV export as a fallback
      const csvContent = this.createCSVFromPermits(searchData.permits)
      const blob = new Blob([csvContent], { type: 'text/csv' })
      return blob
    } catch (error) {
      console.error('Error exporting permits:', error)
      throw error
    }
  }

  private createCSVFromPermits(permits: BuildingPermit[]): string {
    const headers = [
      'Permit Number',
      'Address',
      'Type',
      'Category',
      'Status',
      'Filing Date',
      'Owner',
      'Architect',
    ]
    const rows = permits.map((permit) => [
      permit.permit_number,
      permit.address,
      permit.type,
      permit.category,
      permit.status,
      permit.filing_date.toLocaleDateString(),
      permit.owner || '',
      permit.architect || '',
    ])

    const csvRows = [headers, ...rows]
    return csvRows
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n')
  }
}

export const buildingPermitsService = new BuildingPermitsService()
